import { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useRef } from "react";

const openDialogs = [];
const topDialog = () => openDialogs.find((candidate) => !openDialogs.some((other) => candidate !== other && candidate.contains(other)));
const focusableSelector = [
  "button:not([disabled])", "[href]", "input:not([disabled])", "select:not([disabled])",
  "textarea:not([disabled])", "[tabindex]:not([tabindex='-1'])"
].join(",");

export const UNSAVED_CHANGES_MESSAGE = "Ungespeicherte Änderungen verwerfen?";

export function useUnsavedChanges(dirty, onClose) {
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  return useCallback(() => {
    if (!dirty || window.confirm(UNSAVED_CHANGES_MESSAGE)) onClose();
  }, [dirty, onClose]);
}

export function Button({ variant = "primary", className = "", ...props }) {
  return <button className={`button button-${variant} ${className}`} {...props} />;
}
export function Field({ label, error, hint, id, children }) {
  const description = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  const control = Children.count(children) === 1 && isValidElement(children)
    ? cloneElement(children, { id, "aria-describedby": description, "aria-invalid": error ? true : undefined })
    : children;
  return <div className="field"><label htmlFor={id}>{label}</label>{control}{hint && <small id={`${id}-hint`}>{hint}</small>}{error && <p id={`${id}-error`} className="field-error">{error}</p>}</div>;
}
export function Badge({ tone = "navy", children }) { return <span className={`badge badge-${tone}`}>{children}</span>; }
export function Tooltip({ text, children }) { return <span className="tooltip" tabIndex="0" aria-label={text}>{children}<span role="tooltip">{text}</span></span>; }
export function Card({ title, children, className = "" }) { return <section className={`card ${className}`}>{title && <h2>{title}</h2>}{children}</section>; }
export function Tabs({ items, value, onChange, label }) {
  const move = (event, index) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length;
    onChange(items[next].value);
    event.currentTarget.parentElement.children[next].focus();
  };
  return <div className="segments" role="tablist" aria-label={label}>{items.map((item, index) => <button key={item.value} role="tab" tabIndex={value === item.value ? 0 : -1} aria-selected={value === item.value} onKeyDown={(event) => move(event, index)} onClick={() => onChange(item.value)}>{item.label}</button>)}</div>;
}
export function Dialog({ title, children, onClose, initialFocusRef }) {
  const dialog = useRef(null); const returnFocus = useRef(null); const titleId = useId();
  const closeRef = useRef(onClose); closeRef.current = onClose;
  useEffect(() => {
    const node = dialog.current; const backdrop = node?.parentElement;
    returnFocus.current = document.activeElement;
    openDialogs.push(node);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const background = [...document.body.children].filter((element) => !element.contains(backdrop));
    const previousInert = background.map((element) => element.inert);
    background.forEach((element) => { element.inert = true; });
    const preferred = initialFocusRef?.current || [...node.querySelectorAll("[aria-invalid='true'], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])")].find((element) => !element.closest("header"));
    (preferred || node)?.focus();
    const keyboard = (event) => {
      if (topDialog() !== node) return;
      if (event.key === "Escape") { event.preventDefault(); closeRef.current(); return; }
      if (event.key !== "Tab") return;
      const controls = [...node.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length || process.env.NODE_ENV === "test");
      if (!controls.length) { event.preventDefault(); node.focus(); return; }
      const first = controls[0]; const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keyboard);
    return () => {
      document.removeEventListener("keydown", keyboard);
      const index = openDialogs.lastIndexOf(node); if (index >= 0) openDialogs.splice(index, 1);
      background.forEach((element, position) => { element.inert = previousInert[position]; });
      if (!openDialogs.length) document.body.style.overflow = previousOverflow;
      returnFocus.current?.focus({ preventScroll: true });
    };
  }, [initialFocusRef]);
  const closeTopmost = () => topDialog() === dialog.current && closeRef.current();
  return <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && closeTopmost()}><section ref={dialog} tabIndex="-1" className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}><header><h2 id={titleId}>{title}</h2><button type="button" onClick={closeTopmost} aria-label="Dialog schliessen">×</button></header>{children}</section></div>;
}
export function Notice({ tone = "info", children, onDismiss }) { return <div className={`notice notice-${tone}`} role={tone === "error" ? "alert" : "status"}><span>{children}</span>{onDismiss && <button onClick={onDismiss} aria-label="Meldung schliessen">×</button>}</div>; }
export function DataTable({ columns, rows, empty = "Keine Einträge vorhanden.", label = "Datentabelle" }) { return <div className="table-wrap" role="region" aria-label={label} tabIndex="0"><table><thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead><tbody>{rows.length ? rows.map((row, index) => <tr key={row.id || index}>{columns.map((column) => <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>)}</tr>) : <tr><td colSpan={columns.length} className="table-empty">{empty}</td></tr>}</tbody></table></div>; }
export function WeekNavigation({ label = "Aktuelle Woche", onPrevious, onNext }) { return <div className="week-navigation"><Button variant="secondary" onClick={onPrevious} aria-label="Vorherige Woche">‹</Button><strong>{label}</strong><Button variant="secondary" onClick={onNext} aria-label="Nächste Woche">›</Button></div>; }
